import {
  TrendingUp,
  Heart,
  Sparkles,
  Mail,
  ArrowUpRight,
  MapPin,
  Briefcase,
} from "lucide-react";
import { OPEN_POSITIONS } from "@/data/catalog";
import { COMPANY } from "@/lib/brand";

const PERKS = [
  {
    icon: TrendingUp,
    title: "Growth",
    body: "Structured learning, mentorship and clear progression across functions.",
  },
  {
    icon: Heart,
    title: "Culture",
    body: "Collaborative, respectful and rooted in the responsibility of healthcare.",
  },
  {
    icon: Sparkles,
    title: "Impact",
    body: "Your work directly touches the lives of patients across the country and beyond.",
  },
];

export default function Career() {
  const subject = encodeURIComponent("Career Application — Lenax Pharmaceuticals");

  return (
    <div data-testid="page-career" className="bg-[#F8F9F7]">
      {/* HERO */}
      <section className="border-b border-[#E0E5E2] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="overline text-[#1E8A2E]">Career at Lenax</p>
              <h1 className="font-serif mt-6 text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] text-[#121413]">
                Build the future of{" "}
                <span className="italic text-[#D92D2D]">healthcare</span>, with
                us.
              </h1>
              <p className="mt-8 max-w-xl text-[#4A524D] text-base md:text-lg leading-relaxed">
                At Lenax we don't just make medicines — we build careers around
                them. If you care about quality, science and the patients on the
                other side of every prescription, we'd love to meet you.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href={`mailto:${COMPANY.email}?subject=${subject}`}
                  data-testid="career-apply-cta"
                  className="inline-flex items-center gap-2 bg-[#121413] text-white px-7 py-4 rounded-sm text-sm font-medium hover:bg-[#D92D2D] transition-colors duration-300"
                >
                  Send your CV <ArrowUpRight size={16} />
                </a>
                <a
                  href="#openings"
                  data-testid="career-view-openings"
                  className="inline-flex items-center gap-2 border border-[#121413]/20 text-[#121413] px-7 py-4 rounded-sm text-sm font-medium hover:border-[#1E8A2E] hover:text-[#1E8A2E] transition-colors duration-300"
                >
                  View open roles
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
                <img
                  src="https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg"
                  alt="Team discussing strategy in a modern office"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121413]/40 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="border-b border-[#E0E5E2] py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="overline text-[#1E8A2E]">Why Lenax</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl text-[#121413] tracking-tight leading-tight">
                A meaningful place to build your career.
              </h2>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {PERKS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    data-testid={`career-perk-${i}`}
                    className="border-t border-[#121413] pt-6"
                  >
                    <Icon
                      size={26}
                      className={
                        i % 2 === 0 ? "text-[#D92D2D]" : "text-[#1E8A2E]"
                      }
                    />
                    <h3 className="font-serif text-2xl mt-5 text-[#121413]">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#4A524D] leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* OPENINGS */}
      <section
        id="openings"
        data-testid="career-openings"
        className="border-b border-[#E0E5E2] py-20 md:py-28 scroll-mt-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="overline text-[#1E8A2E]">Sales & Marketing</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl text-[#121413] tracking-tight">
                Roles we're hiring for.
              </h2>
              <p className="mt-4 text-sm text-[#4A524D] max-w-xl leading-relaxed">
                Lenax is currently hiring exclusively across Sales & Marketing
                sub-functions — the roles that keep hospitals supplied and
                clinicians supported.
              </p>
            </div>
            <p className="text-sm text-[#4A524D] max-w-md">
              See a role that fits? Send your CV — we screen every application.
            </p>
          </div>

          <ul className="mt-12 border-t border-[#121413]">
            {OPEN_POSITIONS.map((role, i) => (
              <li
                key={i}
                data-testid={`career-role-${i}`}
                className="group border-b border-[#E0E5E2] py-6 md:py-8 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center"
              >
                <div className="md:col-span-5">
                  <p className="overline text-[#7A827D]">
                    {role.department}
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#121413] tracking-tight group-hover:text-[#1E8A2E] transition-colors">
                    {role.title}
                  </h3>
                </div>
                <div className="md:col-span-3 flex items-center gap-2 text-sm text-[#4A524D]">
                  <MapPin size={14} className="text-[#1E8A2E]" />
                  {role.location}
                </div>
                <div className="md:col-span-2 flex items-center gap-2 text-sm text-[#4A524D]">
                  <Briefcase size={14} className="text-[#D92D2D]" />
                  {role.type}
                </div>
                <div className="md:col-span-2 md:text-right">
                  <a
                    href={`mailto:${COMPANY.email}?subject=${encodeURIComponent(
                      `Application — ${role.title}`,
                    )}`}
                    data-testid={`career-role-apply-${i}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-[#121413] hover:text-[#1E8A2E] transition-colors"
                  >
                    Apply <ArrowUpRight size={14} />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* APPLICATION INSTRUCTIONS — static contact box */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
            <div className="md:col-span-7 bg-[#121413] text-white p-10 md:p-14 grain relative">
              <p className="overline text-[#1E8A2E]">How to apply</p>
              <h3 className="font-serif mt-4 text-3xl md:text-5xl leading-tight">
                Don't see the right role?
                <br />
                We'd still love to hear from you.
              </h3>
              <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                Send us your CV with a short note about the kind of work you're
                excited about. We keep all applications on file and reach out
                when a relevant opening comes up.
              </p>
            </div>
            <div className="md:col-span-5 bg-white p-10 md:p-14">
              <p className="overline text-[#D92D2D]">Careers desk</p>
              <div className="mt-8 space-y-6">
                <a
                  href={`mailto:${COMPANY.email}?subject=${subject}`}
                  data-testid="career-contact-email"
                  className="flex items-start gap-4 group"
                >
                  <span className="inline-flex w-11 h-11 items-center justify-center rounded-sm bg-[#F1F3F0] text-[#1E8A2E] group-hover:bg-[#1E8A2E] group-hover:text-white transition-colors">
                    <Mail size={18} />
                  </span>
                  <span>
                    <span className="overline text-[#7A827D]">Email</span>
                    <span className="block font-serif text-2xl text-[#121413] mt-1 group-hover:text-[#1E8A2E] transition-colors">
                      {COMPANY.email}
                    </span>
                  </span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="inline-flex w-11 h-11 items-center justify-center rounded-sm bg-[#F1F3F0] text-[#121413]">
                    <MapPin size={18} />
                  </span>
                  <span>
                    <span className="overline text-[#7A827D]">Office</span>
                    <span
                      data-testid="career-contact-address"
                      className="block text-sm text-[#121413] mt-2 max-w-xs leading-relaxed"
                    >
                      {COMPANY.address}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
